import { helpers, required, requiredIf } from '@vuelidate/validators';
import { Ref, ref } from 'vue';

import {
  RescheduleReasonDto,
  RescheduleReasonType,
  RescheduleUnavailabilitySpan,
} from '../interfaces/reschedule-reason.interface';
import { TourType } from '../interfaces/tour-type.enum';
import { getShowingAgentCopy } from './showing-agent-copy.util';

export interface RescheduleShowingFormModel {
  showingAgentId: string;
  comment: string;
  date: Date | undefined;
  time: string | undefined;
  reasonType: RescheduleReasonType | null;
  unavailabilitySpan: RescheduleUnavailabilitySpan;
  unavailableFrom: string;
  unavailableUntil: string;
  notReadyUntil: string;
}

export function createRescheduleShowingFormFields(showingAgentId?: string): Ref<RescheduleShowingFormModel> {
  return ref<RescheduleShowingFormModel>({
    date: undefined,
    showingAgentId: showingAgentId as string,
    comment: '',
    time: '8:00 am',
    reasonType: null,
    unavailabilitySpan: RescheduleUnavailabilitySpan.SLOT,
    unavailableFrom: '',
    unavailableUntil: '',
    notReadyUntil: '',
  });
}

/**
 * Everything is required except the renter comment: the reason itself, and the dates a chosen
 * reason reveals. Rules close over the fields ref so the conditional requirements follow the
 * current selection. `tourType` only picks the agent field's message, so that it matches the
 * label that tour type renders.
 */
export function rescheduleShowingFormRules(fields: Ref<RescheduleShowingFormModel>, tourType?: TourType) {
  const isUnavailabilityRange = () =>
    fields.value.reasonType === RescheduleReasonType.CANT_SHOW &&
    fields.value.unavailabilitySpan === RescheduleUnavailabilitySpan.RANGE;
  const isPropertyNotReady = () => fields.value.reasonType === RescheduleReasonType.PROPERTY_NOT_READY;

  return {
    date: { required: helpers.withMessage('You have not selected the date', required) },
    time: { required: helpers.withMessage('Time is required', required) },
    showingAgentId: {
      required: helpers.withMessage(getShowingAgentCopy(tourType).fieldRequiredError, required),
    },
    reasonType: { required: helpers.withMessage('Select why you are rescheduling', required) },
    unavailableFrom: {
      required: helpers.withMessage('Select the first day you are unavailable', requiredIf(isUnavailabilityRange)),
    },
    unavailableUntil: {
      required: helpers.withMessage('Select the day you are available again', requiredIf(isUnavailabilityRange)),
    },
    notReadyUntil: {
      required: helpers.withMessage('Select when the property is ready to show', requiredIf(isPropertyNotReady)),
    },
  };
}

/** Maps the form state to the reason DTO, nulling every field the chosen reason doesn't use. */
export function buildRescheduleReason(fields: RescheduleShowingFormModel): RescheduleReasonDto {
  const isCantShow = fields.reasonType === RescheduleReasonType.CANT_SHOW;
  const isRange = isCantShow && fields.unavailabilitySpan === RescheduleUnavailabilitySpan.RANGE;

  return {
    type: fields.reasonType as RescheduleReasonType,
    span: isCantShow ? fields.unavailabilitySpan : null,
    unavailableFrom: isRange ? fields.unavailableFrom : null,
    unavailableUntil: isRange ? fields.unavailableUntil : null,
    notReadyUntil: fields.reasonType === RescheduleReasonType.PROPERTY_NOT_READY ? fields.notReadyUntil : null,
  };
}
